import type { ScoreSet } from "../../../supabase/functions/_shared/score-set";
import { supabase, invokeEdgeFunction, throwOnError } from "@/lib/supabase-client";
import { normalizeIso } from "@/lib/ladder-portal/hours";

// ─── Types ────────────────────────────────────────────────────────────────────

export type InviteeState =
  | "invited"
  | "interested_no_partner"
  | "pair_pending"
  | "pair_confirmed"
  | "declined"
  | "waitlist";

export type Ladder = {
  id: string;
  slug: string;
  name: string;
  location: string | null;
  startsOn: string;
  endsOn: string;
  weeksCount: number;
  /** Human-facing date, shown in the admin header. Not the gate - see registrationClosesAt. */
  registrationDeadline: string | null;
  /**
   * The exact moment registration closes, as an ISO 8601 instant. This is the gate: the old
   * date-string comparison ran in UTC and left a Bali ladder open until 07:00 the next morning.
   */
  registrationClosesAt: string | null;
  detailsUrl: string | null;
  isActive: boolean;
  createdAt: string;
};

export type Category = {
  id: number;
  ladderId: string;
  key: string;
  label: string;
  targetPairs: number;
  sortOrder: number;
};

export type Invitee = {
  id: number;
  ladderId: string;
  categoryId: number;
  playerId: number | null;
  displayName: string;
  whatsapp: string | null;
  instagram: string | null;
  state: InviteeState;
  pairId: number | null;
  notes: string | null;
  lastContactedAt: string | null;
  invitedAt: string;
  respondedAt: string | null;
  createdAt: string;
  jungleEmail: string | null;
  kitSize: string | null;
  kitWidthCm: number | null;
  kitLengthCm: number | null;
  paidAt: string | null;
  /** The linked player has a player_accounts row, i.e. has signed in at least once. */
  isClaimed: boolean;
  pushOn: boolean;
  installed: boolean;
  acknowledgedAt: string | null;
  /**
   * Lets the organiser resend the exact invite the captain sent. Null once the invitee has
   * claimed, and absent entirely until the sanur-admin function carrying it is deployed.
   */
  claimToken: string | null;
};

export type Pair = {
  id: number;
  ladderId: string;
  categoryId: number;
  inviteeAId: number | null;
  inviteeBId: number | null;
  displayName: string | null;
  isConfirmed: boolean;
  divisionId: number | null;
  seed: number | null;
  createdAt: string;
};

export type Division = {
  id: number;
  ladderId: string;
  categoryId: number;
  name: string;
  sortOrder: number;
};

export type ScheduleState = "unscheduled" | "proposed" | "confirmed" | "played";

export type Fixture = {
  id: number;
  ladderId: string;
  divisionId: number;
  weekNo: number;
  homePairId: number;
  awayPairId: number;
  status: "scheduled" | "completed";
  scoreSets: ScoreSet[] | null;
  winnerPairId: number | null;
  challengerPairId: number | null;
  playedAt: string | null;
  scheduledAt: string | null;
  venueId: number | null;
  note: string | null;
  submittedAt: string | null;
  submittedBy: "admin" | "player" | null;
  isWalkover: boolean;
  walkoverReason: string | null;
  isDraw: boolean;
  createdAt: string;
  // Spec §4.5 — the proposal lifecycle. `status`/`scheduledAt` keep their meaning for standings.
  scheduleState: ScheduleState;
  proposedSlotId: number | null;
  proposedStartsAt: string | null;
  /** a confirmed time without a club court: the pair that confirmed first, who book the court at Jungle */
  bookingPairId: number | null;
  /** a no-court time: the person who confirmed first (ticked "a court is free") books it at Jungle */
  bookingPlayerId?: number | null;
  proposedAt: string | null;
  proposalExpiresAt: string | null;
  /** null = the matcher proposed it. */
  proposedByPlayerId: number | null;
  homeConfirmedByPlayerId: number | null;
  awayConfirmedByPlayerId: number | null;
  adHocBooking: boolean;
  /** Own booking: the Jungle court they booked (Set the time); null for a league court or an old own booking. */
  ownCourtLabel?: string | null;
  submittedByPlayerId: number | null;
  resultConfirmedByPlayerId: number | null;
};

export type FixtureDispute = {
  id: number;
  fixtureId: number;
  raisedBy: string;
  reason: string;
  status: "open" | "resolved";
  resolutionNote: string | null;
  resolvedAt: string | null;
  createdAt: string;
};

export type PadelClub = { id: number; name: string };

export type { ScoreSet } from "../../../supabase/functions/_shared/score-set";

export type StandingsRow = {
  divisionId: number;
  pairId: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  setDiff: number;
  gameDiff: number;
  points: number;
  rank: number;
};

// ─── Row mappers ──────────────────────────────────────────────────────────────

type RawLadder = {
  id: string;
  slug: string;
  name: string;
  location: string | null;
  starts_on: string;
  ends_on: string;
  weeks_count: number;
  registration_deadline: string | null;
  registration_closes_at: string | null;
  details_url: string | null;
  is_active: boolean;
  created_at: string;
};

type RawCategory = {
  id: number;
  ladder_id: string;
  key: string;
  label: string;
  target_pairs: number;
  sort_order: number;
};

type RawInvitee = {
  id: number;
  ladder_id: string;
  category_id: number;
  player_id: number | null;
  display_name: string;
  whatsapp: string | null;
  instagram: string | null;
  state: InviteeState;
  pair_id: number | null;
  notes: string | null;
  last_contacted_at: string | null;
  invited_at: string;
  responded_at: string | null;
  created_at: string;
  jungle_padel_email: string | null;
  kit_size: string | null;
  kit_width_cm: number | null;
  kit_length_cm: number | null;
  paid_at: string | null;
  is_claimed: boolean;
  push_on?: boolean;
  installed?: boolean;
  acknowledged_at: string | null;
  /** Only present for an unclaimed row; the server withholds it once the account is attached. */
  claim_token?: string | null;
};

type RawPair = {
  id: number;
  ladder_id: string;
  category_id: number;
  invitee_a_id: number | null;
  invitee_b_id: number | null;
  display_name: string | null;
  is_confirmed: boolean;
  division_id: number | null;
  seed: number | null;
  created_at: string;
};

type RawDivision = {
  id: number;
  ladder_id: string;
  category_id: number;
  name: string;
  sort_order: number;
};

export type RawFixture = {
  id: number;
  ladder_id: string;
  division_id: number;
  week_no: number;
  home_pair_id: number;
  away_pair_id: number;
  status: "scheduled" | "completed";
  score_sets: ScoreSet[] | null;
  winner_pair_id: number | null;
  challenger_pair_id: number | null;
  played_at: string | null;
  scheduled_at: string | null;
  venue_id: number | null;
  note: string | null;
  submitted_at: string | null;
  submitted_by: string | null;
  is_walkover: boolean;
  walkover_reason: string | null;
  is_draw: boolean;
  created_at: string;
  schedule_state?: ScheduleState | null;
  proposed_slot_id?: number | null;
  proposed_starts_at?: string | null;
  booking_pair_id?: number | null;
  booking_player_id?: number | null;
  proposed_at?: string | null;
  proposal_expires_at?: string | null;
  proposed_by_player_id?: number | null;
  home_confirmed_by_player_id?: number | null;
  away_confirmed_by_player_id?: number | null;
  ad_hoc_booking?: boolean | null;
  own_court_label?: string | null;
  submitted_by_player_id?: number | null;
  result_confirmed_by_player_id?: number | null;
};

type RawDispute = {
  id: number;
  fixture_id: number;
  raised_by: string;
  reason: string;
  status: "open" | "resolved";
  resolution_note: string | null;
  resolved_at: string | null;
  created_at: string;
};

const mapLadder = (r: RawLadder): Ladder => ({
  id: r.id,
  slug: r.slug,
  name: r.name,
  location: r.location,
  startsOn: r.starts_on,
  endsOn: r.ends_on,
  weeksCount: r.weeks_count,
  registrationDeadline: r.registration_deadline,
  registrationClosesAt: r.registration_closes_at ?? null,
  detailsUrl: r.details_url || null,
  isActive: r.is_active,
  createdAt: r.created_at,
}) satisfies Ladder;

const mapCategory = (r: RawCategory): Category => ({
  id: r.id,
  ladderId: r.ladder_id,
  key: r.key,
  label: r.label,
  targetPairs: r.target_pairs,
  sortOrder: r.sort_order,
}) satisfies Category;

const mapInvitee = (r: RawInvitee): Invitee => ({
  id: r.id,
  ladderId: r.ladder_id,
  categoryId: r.category_id,
  playerId: r.player_id,
  displayName: r.display_name,
  whatsapp: r.whatsapp,
  instagram: r.instagram,
  state: r.state,
  pairId: r.pair_id,
  notes: r.notes,
  lastContactedAt: r.last_contacted_at,
  invitedAt: r.invited_at,
  respondedAt: r.responded_at,
  createdAt: r.created_at,
  jungleEmail: r.jungle_padel_email,
  kitSize: r.kit_size,
  kitWidthCm: r.kit_width_cm,
  kitLengthCm: r.kit_length_cm,
  paidAt: r.paid_at ?? null,
  isClaimed: Boolean(r.is_claimed),
  pushOn: r.push_on === true,
  installed: r.installed === true,
  acknowledgedAt: r.acknowledged_at ?? null,
  claimToken: r.claim_token ?? null,
}) satisfies Invitee;

const mapPair = (r: RawPair): Pair => ({
  id: r.id,
  ladderId: r.ladder_id,
  categoryId: r.category_id,
  inviteeAId: r.invitee_a_id,
  inviteeBId: r.invitee_b_id,
  displayName: r.display_name,
  isConfirmed: r.is_confirmed,
  divisionId: r.division_id,
  seed: r.seed,
  createdAt: r.created_at,
}) satisfies Pair;

const mapDivision = (r: RawDivision): Division => ({
  id: r.id,
  ladderId: r.ladder_id,
  categoryId: r.category_id,
  name: r.name,
  sortOrder: r.sort_order,
}) satisfies Division;

const legacyScheduleState = (r: RawFixture): ScheduleState =>
  r.status === "completed" ? "played" : r.scheduled_at ? "confirmed" : "unscheduled";

export const mapFixture = (r: RawFixture): Fixture => ({
  id: r.id,
  ladderId: r.ladder_id,
  divisionId: r.division_id,
  weekNo: r.week_no,
  homePairId: r.home_pair_id,
  awayPairId: r.away_pair_id,
  status: r.status,
  scoreSets: r.score_sets,
  winnerPairId: r.winner_pair_id,
  challengerPairId: r.challenger_pair_id,
  playedAt: r.played_at,
  scheduledAt: r.scheduled_at ?? null,
  venueId: r.venue_id,
  note: r.note,
  submittedAt: r.submitted_at ?? null,
  submittedBy: (r.submitted_by === "admin" || r.submitted_by === "player" ? r.submitted_by : null),
  isWalkover: r.is_walkover ?? false,
  walkoverReason: r.walkover_reason ?? null,
  isDraw: r.is_draw ?? false,
  createdAt: r.created_at,
  scheduleState: r.schedule_state ?? legacyScheduleState(r),
  proposedSlotId: r.proposed_slot_id ?? null,
  proposedStartsAt: r.proposed_starts_at ? normalizeIso(r.proposed_starts_at) : null,
  bookingPairId: r.booking_pair_id ?? null,
  bookingPlayerId: r.booking_player_id ?? null,
  proposedAt: r.proposed_at ?? null,
  proposalExpiresAt: r.proposal_expires_at ?? null,
  proposedByPlayerId: r.proposed_by_player_id ?? null,
  homeConfirmedByPlayerId: r.home_confirmed_by_player_id ?? null,
  awayConfirmedByPlayerId: r.away_confirmed_by_player_id ?? null,
  adHocBooking: r.ad_hoc_booking ?? false,
  ownCourtLabel: r.own_court_label ?? null,
  submittedByPlayerId: r.submitted_by_player_id ?? null,
  resultConfirmedByPlayerId: r.result_confirmed_by_player_id ?? null,
}) satisfies Fixture;

const mapDispute = (r: RawDispute): FixtureDispute => ({
  id: r.id,
  fixtureId: r.fixture_id,
  raisedBy: r.raised_by,
  reason: r.reason,
  status: r.status,
  resolutionNote: r.resolution_note,
  resolvedAt: r.resolved_at,
  createdAt: r.created_at,
}) satisfies FixtureDispute;

// ─── Read functions (direct supabase client) ──────────────────────────────────

export const fetchLadders = async (): Promise<Ladder[]> => {
  const { data, error } = await supabase
    .schema("ladders")
    .from("ladders")
    .select("id, slug, name, location, starts_on, ends_on, weeks_count, registration_deadline, registration_closes_at, details_url, is_active, created_at")
    .order("starts_on", { ascending: false });
  if (error) throwOnError(error, "Unable to load ladders");
  return (data ?? []).map((r) => mapLadder(r as RawLadder));
};

/**
 * Ladders still accepting registrations, soonest deadline first. Drives every "register now"
 * prompt in the app, so a ladder with no close time set is deliberately absent: an open-ended
 * registration is not something to advertise a countdown for.
 */
export const fetchOpenLadders = async (): Promise<Ladder[]> => {
  const { data, error } = await supabase
    .schema("ladders")
    .from("ladders")
    .select("id, slug, name, location, starts_on, ends_on, weeks_count, registration_deadline, registration_closes_at, details_url, is_active, created_at")
    .eq("is_active", true)
    .gt("registration_closes_at", new Date().toISOString())
    .order("registration_closes_at", { ascending: true });
  if (error) throwOnError(error, "Unable to load open registrations");
  return (data ?? []).map((r) => mapLadder(r as RawLadder));
};

export const fetchLadderBySlug = async (slug: string): Promise<Ladder | null> => {
  const { data, error } = await supabase
    .schema("ladders")
    .from("ladders")
    .select("id, slug, name, location, starts_on, ends_on, weeks_count, registration_deadline, registration_closes_at, details_url, is_active, created_at")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throwOnError(error, "Unable to load ladder");
  return data ? mapLadder(data as RawLadder) : null;
};

export const fetchCategories = async (ladderId: string): Promise<Category[]> => {
  const { data, error } = await supabase
    .schema("ladders")
    .from("categories")
    .select("id, ladder_id, key, label, target_pairs, sort_order")
    .eq("ladder_id", ladderId)
    .order("sort_order", { ascending: true });
  if (error) throwOnError(error, "Unable to load categories");
  return (data ?? []).map((r) => mapCategory(r as RawCategory));
};

export type PublicInvitee = {
  id: number;
  ladderId: string;
  categoryId: number;
  playerId: number | null;
  displayName: string;
  state: InviteeState;
  pairId: number | null;
  createdAt: string;
};

export const fetchPublicInvitees = async (ladderId: string): Promise<PublicInvitee[]> => {
  const { data, error } = await supabase
    .schema("ladders")
    .from("v_invitees_public")
    .select("id, ladder_id, category_id, player_id, display_name, state, pair_id, created_at")
    .eq("ladder_id", ladderId)
    .order("created_at", { ascending: true });
  if (error) throwOnError(error, "Unable to load registrations");
  return (data ?? []).map((r) => ({
    id: r.id as number,
    ladderId: r.ladder_id as string,
    categoryId: r.category_id as number,
    playerId: r.player_id as number | null,
    displayName: r.display_name as string,
    state: r.state as InviteeState,
    pairId: r.pair_id as number | null,
    createdAt: r.created_at as string,
  }) satisfies PublicInvitee);
};

export const fetchInvitees = async (ladderId: string): Promise<Invitee[]> => {
  const result = await invokeEdgeFunction<{ invitees: RawInvitee[] }>(
    "sanur-admin",
    { resource: "invitee", action: "list", ladderId },
    true,
  );
  return (result.invitees ?? []).map(mapInvitee);
};

export const fetchPairs = async (
  ladderId: string,
  opts: { confirmedOnly?: boolean } = {},
): Promise<Pair[]> => {
  let query = supabase
    .schema("ladders")
    .from("pairs")
    .select("id, ladder_id, category_id, invitee_a_id, invitee_b_id, display_name, is_confirmed, division_id, seed, created_at")
    .eq("ladder_id", ladderId);
  if (opts.confirmedOnly) query = query.eq("is_confirmed", true);
  const { data, error } = await query.order("created_at", { ascending: true });
  if (error) throwOnError(error, "Unable to load pairs");
  return (data ?? []).map((r) => mapPair(r as RawPair));
};

export const fetchDivisions = async (
  ladderId: string,
  opts: { categoryId?: number } = {},
): Promise<Division[]> => {
  let query = supabase
    .schema("ladders")
    .from("divisions")
    .select("id, ladder_id, category_id, name, sort_order")
    .eq("ladder_id", ladderId);
  if (opts.categoryId) query = query.eq("category_id", opts.categoryId);
  const { data, error } = await query.order("sort_order", { ascending: true });
  if (error) throwOnError(error, "Unable to load divisions");
  return (data ?? []).map((r) => mapDivision(r as RawDivision));
};

export const FIXTURE_SELECT =
  "id, ladder_id, division_id, week_no, home_pair_id, away_pair_id, status, score_sets, winner_pair_id, challenger_pair_id, played_at, scheduled_at, venue_id, note, submitted_at, submitted_by, is_walkover, walkover_reason, is_draw, created_at, " +
  "schedule_state, proposed_slot_id, proposed_starts_at, booking_pair_id, booking_player_id, proposed_at, proposal_expires_at, proposed_by_player_id, home_confirmed_by_player_id, away_confirmed_by_player_id, ad_hoc_booking, own_court_label, submitted_by_player_id, result_confirmed_by_player_id";

export const fetchFixtures = async (
  ladderId: string,
  opts: { divisionId?: number; weekNo?: number } = {},
): Promise<Fixture[]> => {
  let query = supabase
    .schema("ladders")
    .from("fixtures")
    .select(FIXTURE_SELECT)
    .eq("ladder_id", ladderId);
  if (opts.divisionId) query = query.eq("division_id", opts.divisionId);
  if (opts.weekNo) query = query.eq("week_no", opts.weekNo);
  const { data, error } = await query.order("week_no").order("id");
  if (error) throwOnError(error, "Unable to load fixtures");
  return (data ?? []).map((r) => mapFixture(r as unknown as RawFixture));
};

export const fetchFixtureDisputes = async (ladderId: string): Promise<FixtureDispute[]> => {
  const result = await invokeEdgeFunction<{ disputes: RawDispute[] }>(
    "sanur-admin",
    { resource: "dispute", action: "list", ladderId },
    true,
  );
  return (result.disputes ?? []).map(mapDispute);
};

type RawStandingsRow = {
  division_id: number;
  pair_id: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  set_diff: number;
  game_diff: number;
  points: number;
  rank: number;
};

const mapStandingsRow = (r: RawStandingsRow): StandingsRow => ({
  divisionId: r.division_id,
  pairId: r.pair_id,
  played: r.played,
  wins: r.wins,
  draws: r.draws ?? 0,
  losses: r.losses,
  setDiff: r.set_diff,
  gameDiff: r.game_diff,
  points: r.points,
  rank: r.rank,
});

export const fetchStandings = async (ladderId: string, divisionId: number): Promise<StandingsRow[]> => {
  const { data, error } = await supabase
    .schema("ladders")
    .from("v_standings")
    .select("division_id, pair_id, played, wins, draws, losses, set_diff, game_diff, points, rank")
    .eq("division_id", divisionId);
  if (error) throwOnError(error, "Unable to load standings");
  return (data ?? []).map(mapStandingsRow);
};

export const fetchAllStandings = async (ladderId: string, divisionIds: number[]): Promise<StandingsRow[]> => {
  if (divisionIds.length === 0) return [];
  const { data, error } = await supabase
    .schema("ladders")
    .from("v_standings")
    .select("division_id, pair_id, played, wins, draws, losses, set_diff, game_diff, points, rank")
    .in("division_id", divisionIds);
  if (error) throwOnError(error, "Unable to load standings");
  return (data ?? []).map(mapStandingsRow);
};

// ─── Admin write functions (via sanur-admin edge function) ────────────────────

export const adminCreateLadder = (payload: {
  id?: string;
  slug?: string;
  name: string;
  location?: string;
  startsOn: string;
  endsOn: string;
  weeksCount?: number;
  registrationDeadline?: string | null;
  detailsUrl?: string | null;
  isActive?: boolean;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "ladder", action: "upsert", ...payload }, true);

/**
 * Change (or clear) an existing ladder's registration deadline. Separate from the upsert, which is
 * keyed on a name-derived id and rewrites the whole row - using that to move one date would reset
 * the others. Both the display date and the gate timestamp move together.
 */
export const adminSetLadderDeadline = (payload: { id: string; registrationDeadline: string | null }) =>
  invokeEdgeFunction<{ id: string; registrationDeadline: string | null }>(
    "sanur-admin", { resource: "ladder", action: "setDeadline", ...payload }, true,
  );

export const adminUpsertCategory = (payload: {
  id?: number;
  ladderId: string;
  key: string;
  label: string;
  targetPairs?: number;
  sortOrder?: number;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "category", action: "upsert", ...payload }, true);

export const adminCreateInvitee = (payload: {
  ladderId: string;
  categoryId: number;
  displayName: string;
  playerId?: number | null;
  whatsapp?: string | null;
  instagram?: string | null;
  state?: InviteeState;
  notes?: string | null;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "invitee", action: "create", ...payload }, true);

export const adminBulkCreateInvitees = (payload: {
  ladderId: string;
  categoryId: number;
  entries: Array<{
    displayName: string;
    playerId?: number | null;
    whatsapp?: string | null;
    instagram?: string | null;
    notes?: string | null;
  }>;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "invitee", action: "bulkCreate", ...payload }, true);

export const adminUpdateInvitee = (payload: {
  ladderId: string;
  id: number;
  displayName?: string;
  whatsapp?: string | null;
  instagram?: string | null;
  notes?: string | null;
  state?: InviteeState;
  pairId?: number | null;
  playerId?: number | null;
  categoryId?: number;
  lastContactedAt?: string | boolean | null;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "invitee", action: "update", ...payload }, true);

export const adminSetInviteePaid = (payload: { ladderId: string; id: number; paid: boolean }) =>
  invokeEdgeFunction<{ updated: true; id: number; paidAt: string | null }>(
    "sanur-admin", { resource: "invitee", action: "setPaid", ...payload }, true,
  );

export const adminDeleteInvitee = (payload: { ladderId: string; id: number }) =>
  invokeEdgeFunction("sanur-admin", { resource: "invitee", action: "delete", ...payload }, true);

export const adminCreatePair = (payload: {
  ladderId: string;
  categoryId: number;
  inviteeAId: number;
  inviteeBId: number;
  displayName?: string;
  isConfirmed?: boolean;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "pair", action: "create", ...payload }, true);

export const adminDeletePair = (payload: { ladderId: string; id: number }) =>
  invokeEdgeFunction("sanur-admin", { resource: "pair", action: "delete", ...payload }, true);

export const adminReplacePlayerInPair = (payload: {
  ladderId: string;
  id: number;
  slot: "a" | "b";
  newInviteeId: number;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "pair", action: "replace-player", ...payload }, true);

// ─── Who plays (subs + who-played snapshot) ──────────────────────────────────

export type LineupSlot = {
  fixtureId: number; side: "home" | "away"; slot: "a" | "b"; playerId: number | null; displayName: string | null;
  /** A sub stands in for outName in this one match. */
  isSub: boolean; outName: string | null;
  /** The pair member this slot belongs to (who sits out when there is a sub): what admin_set_sub takes. */
  baseInviteeId: number | null;
};

/** Who plays each fixture: the result's snapshot once played, else the pairs now, with any sub on top. Names only. */
export const fetchFixtureLineups = async (fixtureIds: number[]): Promise<LineupSlot[]> => {
  if (!fixtureIds.length) return [];
  const { data, error } = await supabase.schema("ladders").rpc("fixture_lineups", { p_fixture_ids: fixtureIds });
  if (error) throwOnError(error, "Unable to load who plays");
  return (data ?? []) as unknown as LineupSlot[];
};

/** Admin: subPlayerId plays instead of outInviteeId in this match; null removes the sub. */
export const adminSetSub = (payload: { fixtureId: number; outInviteeId: number; subPlayerId: number | null }) =>
  invokeEdgeFunction("sanur-admin", { resource: "fixtures", action: "setSub", ...payload }, true);

export const adminUpsertDivision = (payload: {
  ladderId: string;
  id?: number;
  categoryId: number;
  name: string;
  sortOrder?: number;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "division", action: "upsert", ...payload }, true);

export const adminDeleteDivision = (payload: { ladderId: string; id: number }) =>
  invokeEdgeFunction("sanur-admin", { resource: "division", action: "delete", ...payload }, true);

export const adminAssignPairToDivision = (payload: {
  ladderId: string;
  divisionId: number;
  pairId: number;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "division", action: "assignPair", ...payload }, true);

export const adminUnassignPairFromDivision = (payload: { ladderId: string; pairId: number }) =>
  invokeEdgeFunction("sanur-admin", { resource: "division", action: "unassignPair", ...payload }, true);

export const adminBulkAssignPairs = (payload: {
  ladderId: string;
  assignments: Array<{ pairId: number; divisionId: number | null }>;
}) =>
  invokeEdgeFunction<{ assigned: number }>(
    "sanur-admin", { resource: "division", action: "bulkAssign", ...payload }, true,
  );

export const adminGenerateSchedule = (payload: {
  ladderId: string;
  divisionId: number;
  force?: boolean;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "schedule", action: "generate", ...payload }, true);

export const adminUpdateFixture = (payload: {
  ladderId: string;
  id: number;
  weekNo?: number;
  scoreSets?: ScoreSet[];
  homePairId?: number;
  awayPairId?: number;
  note?: string | null;
  challengerPairId?: number | null;
  isWalkover?: boolean;
  walkoverReason?: string | null;
  isDraw?: boolean;
  playedAt?: string;
  venueId?: number | null;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "fixture", action: "update", ...payload }, true);

export const adminResolveDispute = (payload: {
  ladderId: string;
  id: number;
  resolutionNote?: string | null;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "dispute", action: "resolve", ...payload }, true);

export const adminClearFixtureResult = (payload: { ladderId: string; id: number }) =>
  invokeEdgeFunction("sanur-admin", { resource: "fixture", action: "clearResult", ...payload }, true);

export const adminDeleteFixture = (payload: { ladderId: string; id: number }) =>
  invokeEdgeFunction("sanur-admin", { resource: "fixture", action: "delete", ...payload }, true);

// ─── Courts / events (spec §10, contract §3) ──────────────────────────────────

export type SlotRow = { startsAt: string; endsAt: string; courtLabel: string; status: "held" | "skipped"; note: string | null; /** only when the sheet has a "Booked by JPS" column */ namesEntered?: boolean };
/** Who plays a confirmed slot, with Jungle emails (the club sheet's "Players" and "Jungle emails" columns). */
export type AdminSlotFixture = { pairs: AdhocFixture["pairs"] | null };
export type AdminSlot = { id: number; startsAt: string; endsAt: string; courtLabel: string; status: "held" | "released" | "booked"; fixtureId: number | null; sourceNote: string | null; namesEnteredAt: string | null; fixture?: AdminSlotFixture | null };
export type AdminEvent = { id: number; fixtureId: number | null; playerId: number | null; kind: string; payload: Record<string, unknown>; createdAt: string };
export type AdhocFixture = { id: number; week_no?: number; scheduled_at: string; pairs: Array<{ display_name: string; players: Array<{ name: string; jungle_padel_email: string | null }> }> };

type RawAdminSlot = { id: number; starts_at: string; ends_at: string; court_label: string; status: AdminSlot["status"]; fixture_id: number | null; source_note: string | null; names_entered_at: string | null; fixture?: { pairs: AdhocFixture["pairs"] | null } | null };
const mapAdminSlot = (r: RawAdminSlot): AdminSlot => ({
  id: r.id, startsAt: new Date(r.starts_at).toISOString(), endsAt: new Date(r.ends_at).toISOString(), courtLabel: r.court_label, status: r.status, fixtureId: r.fixture_id, sourceNote: r.source_note,
  namesEnteredAt: r.names_entered_at ? new Date(r.names_entered_at).toISOString() : null,
  fixture: r.fixture ? { pairs: r.fixture.pairs } : null,
});
type RawAdminEvent = { id: number; fixture_id: number | null; player_id: number | null; kind: string; payload: Record<string, unknown> | null; created_at: string };

export const adminImportCourts = (payload: { ladderId: string; text: string; dryRun?: boolean }) =>
  invokeEdgeFunction<{ parsed: SlotRow[]; imported: number; skipped: number; duplicates: number }>("sanur-admin", { resource: "courts", action: "import", ...payload }, true);

export const adminListCourts = async (payload: { ladderId: string; weekStart?: string }): Promise<AdminSlot[]> => {
  const r = await invokeEdgeFunction<{ slots: RawAdminSlot[] }>("sanur-admin", { resource: "courts", action: "list", ...payload }, true);
  return (r.slots ?? []).map(mapAdminSlot);
};

export const adminAddCourt = async (payload: { ladderId: string; startsAt: string; endsAt: string; courtLabel: string }): Promise<AdminSlot> => {
  const r = await invokeEdgeFunction<{ slot: RawAdminSlot }>("sanur-admin", { resource: "courts", action: "add", ...payload }, true);
  return mapAdminSlot(r.slot);
};

export const adminReleaseCourt = (payload: { slotId: number; reason: string }) =>
  invokeEdgeFunction<{ ok: true; unproposedFixtureId: number | null }>("sanur-admin", { resource: "courts", action: "release", ...payload }, true);

export const adminSetNamesEntered = (payload: { slotId: number; on: boolean }) =>
  invokeEdgeFunction<{ slot: RawAdminSlot }>("sanur-admin", { resource: "courts", action: "setNamesEntered", ...payload }, true);

export type CourtChange =
  | { op: "add"; startsAt: string; endsAt: string; courtLabel: string }
  | { op: "release"; slotId: number; startsAt: string; courtLabel: string; reason: string };

/** Free-text court changes: read the note into a previewable list (nothing is applied yet). */
export const adminParseCourtText = (ladderId: string, text: string) =>
  invokeEdgeFunction<{ changes: CourtChange[]; readback: string; unmatched: string[] }>("sanur-admin", { resource: "courts", action: "parseText", ladderId, text }, true);

export const adminApplyCourtText = (ladderId: string, changes: CourtChange[], text?: string) =>
  invokeEdgeFunction<{ added: number; released: number; skipped: string[] }>("sanur-admin", { resource: "courts", action: "applyText", ladderId, changes, text }, true);

export const adminListEvents = async (payload: { ladderId: string; playerId?: number; fixtureId?: number; kind?: string; excludeKinds?: string[]; limit?: number }): Promise<AdminEvent[]> => {
  const r = await invokeEdgeFunction<{ events: RawAdminEvent[] }>("sanur-admin", { resource: "events", action: "list", ...payload }, true);
  return (r.events ?? []).map((e) => ({ id: e.id, fixtureId: e.fixture_id, playerId: e.player_id, kind: e.kind, payload: e.payload ?? {}, createdAt: e.created_at }));
};

export type BlockerWeekState = "set" | "cant_play" | "unset" | "unlinked";
export type BlockerPerson = { inviteeId: number; playerId: number | null; displayName: string; whatsapp: string | null; pairId: number; weekState: BlockerWeekState; confirmedPair: boolean };
export type BlockerRow = {
  fixtureId: number; weekNo: number; divisionId: number; homePairId: number; awayPairId: number; scheduleState: ScheduleState;
  proposedStartsAt: string | null; proposalExpiresAt: string | null; scheduledAt: string | null; people: BlockerPerson[];
};
export type BlockersResult = { fixtures: BlockerRow[]; weekStartByNo: Record<string, string> };

export const adminListBlockers = async (payload: { ladderId: string }): Promise<BlockersResult> => {
  const r = await invokeEdgeFunction<BlockersResult>("sanur-admin", { resource: "blockers", action: "list", ...payload }, true);
  return { fixtures: r.fixtures ?? [], weekStartByNo: r.weekStartByNo ?? {} };
};

export const adminAdhocList = async (payload: { ladderId: string }): Promise<AdhocFixture[]> => {
  const r = await invokeEdgeFunction<{ fixtures: AdhocFixture[] }>("sanur-admin", { resource: "fixtures", action: "adhocList", ...payload }, true);
  return r.fixtures ?? [];
};

// ─── Public write functions (via ladders-public edge function) ───────────────

/** Kit size for this season's order: a chart label, or CUSTOM with explicit dimensions. */
export type KitSelection = { kitSize: string; kitWidthCm?: number | null; kitLengthCm?: number | null };

/** Which season-terms statements the player ticked, and against which version of the copy. */
export type Acknowledgements = { version: string; keys: string[] };

/**
 * Each person answers for themselves. The signed-in caller can give their own kit size and booking
 * email while they are already on the form, but they are never asked for their partner's - nobody
 * reliably knows someone else's shirt size. Anything left blank is filled in later from the player's
 * own home page via `updateMyRegistration`.
 */
type RegistrantContact = { whatsapp: string };
type OwnDetails = RegistrantContact & { jungleEmail?: string } & Partial<KitSelection>;

/** Partner: an existing player picked from search, or typed details for someone not in the database. */
export type PartnerInput =
  ({ playerId: number } | { firstName: string; lastName: string; level: string }) & RegistrantContact;

/**
 * The signed-in caller is player A; the edge function reads their identity from the JWT.
 * `attached` means no new pair was created: the caller stepped into a registration their partner
 * had already made for them (or paired up with a partner who had signed up alone), so there is
 * no invite token to pass on.
 */
export const registerPair = (
  payload: { ladderId: string; categoryId: number; playerB: PartnerInput; acknowledgements?: Acknowledgements } & OwnDetails,
) =>
  invokeEdgeFunction<{ registered: true; pairId: number; names: [string, string]; partnerClaimToken: string | null; attached?: boolean; partnerName?: string | null }>(
    "ladders-public", { resource: "pair", action: "register", ...payload }, false,
  );

/**
 * Step into a registration a captain typed for you, matched by phone or name, when the invite
 * link never made it this far. One ladder, or every open ladder when none is given.
 */
export const linkMyRegistration = (payload: { ladderId?: string }) =>
  invokeEdgeFunction<{ linked: boolean; ladderIds: string[] }>(
    "ladders-public", { resource: "invitee", action: "linkMine", ...payload }, false,
  );

/**
 * Attach your own claimed player to a registration someone entered for you. The token arrives in the
 * invite link the captain sends; a hand-entered partner has no player_id, so this is the only way
 * their registration ever becomes reachable.
 */
export type ClaimTokenResult =
  /**
   * A registration someone typed for you: your claimed player is now attached to it.
   * `relinkedPlayer` means your account was moved onto the profile the captain picked for you,
   * so anything cached about your player is stale.
   */
  | { claimed: true; ladderId: string; alreadyLinked: boolean; relinkedPlayer?: boolean }
  /** A solo registrant's open invitation - accept it with pairWithMyRegistration. */
  | { claimed: false; kind: "pairing"; ladderId: string; hostName: string };

export const claimMyRegistration = (payload: { token: string; acknowledgements?: Acknowledgements }) =>
  invokeEdgeFunction<ClaimTokenResult>(
    "ladders-public", { resource: "invitee", action: "claim", ...payload }, false,
  );

export const registerSolo = (
  payload: { ladderId: string; categoryId: number; acknowledgements?: Acknowledgements } & OwnDetails,
) =>
  invokeEdgeFunction<{ registered: true; inviteeId: number; name: string; claimToken: string | null; attached?: boolean; partnerName?: string | null }>(
    "ladders-public", { resource: "solo", action: "register", ...payload }, false,
  );

/**
 * Accept a solo registrant's open invitation. They signed up alone and then found their own
 * partner, so the organiser is not in the loop: this reuses the caller's own solo row for this
 * ladder if they have one, creates it if they do not, and pairs the two.
 */
/**
 * Your own current invite link for a ladder, or null once you are paired. Tokens rotate when they
 * are spent, so the one handed out at registration can go stale - this is how a player who is
 * still looking for a partner gets the live one.
 */
export const fetchMyInviteToken = (payload: { ladderId: string }) =>
  invokeEdgeFunction<{ claimToken: string | null }>(
    "ladders-public", { resource: "invitee", action: "myInviteToken", ...payload }, false,
  );

export const pairWithMyRegistration = (payload: { token: string; whatsapp: string; acknowledgements?: Acknowledgements }) =>
  invokeEdgeFunction<{ paired: true; ladderId: string; partnerName: string }>(
    "ladders-public", { resource: "invitee", action: "pairWithMe", ...payload }, false,
  );

/** Update your own registration for a ladder. Only the fields you pass are changed. */
export const updateMyRegistration = (
  payload: { ladderId: string; whatsapp?: string; jungleEmail?: string | null; acknowledgements?: Acknowledgements } & Partial<KitSelection>,
) =>
  invokeEdgeFunction<{ updated: true }>(
    "ladders-public", { resource: "invitee", action: "updateMine", ...payload }, false,
  );

export const fetchPadelClubs = async (): Promise<PadelClub[]> => {
  const { data, error } = await supabase
    .schema("venues")
    .from("padel_clubs")
    .select("club_id, club_name")
    .order("club_name");
  if (error) throwOnError(error, "Unable to load venues");
  return (data ?? []).map((r) => ({ id: r.club_id as number, name: r.club_name as string }));
};

export const submitFixtureScore = (payload: {
  fixtureId: number;
  scoreSets?: ScoreSet[];
  playedAt?: string | null;
  venueId?: number | null;
  isWalkover?: boolean;
  walkoverReason?: string | null;
  walkoverWinner?: "home" | "away";
  courtLabel?: string | null;
}) =>
  invokeEdgeFunction("ladders-public", { resource: "fixture", action: "submitScore", ...payload }, false);

export const editFixtureScore = (payload: {
  fixtureId: number;
  scoreSets?: ScoreSet[];
  playedAt?: string | null;
  venueId?: number | null;
  isWalkover?: boolean;
  walkoverReason?: string | null;
  walkoverWinner?: "home" | "away";
}) =>
  invokeEdgeFunction("ladders-public", { resource: "fixture", action: "editScore", ...payload }, false);

export const adminSetFixtureSchedule = (payload: {
  ladderId: string;
  id: number;
  scheduledAt: string | null;
  venueId: number | null;
}) =>
  invokeEdgeFunction("sanur-admin", { resource: "fixture", action: "setSchedule", ...payload }, true);

export const generateMessage = (
  ladderId: string,
  params: { summaryType: "division"; divisionId: number; weekNo?: number }
    | { summaryType: "community"; weekNo?: number }
    | { summaryType: "season" },
) => {
  type Payload =
    | { ladder_id: string; summary_type: "division"; division_id: number; week_no?: number }
    | { ladder_id: string; summary_type: "community"; week_no?: number }
    | { ladder_id: string; summary_type: "season" };
  let payload: Payload;
  if (params.summaryType === "division") {
    payload = { ladder_id: ladderId, summary_type: "division", division_id: params.divisionId, ...(params.weekNo !== undefined ? { week_no: params.weekNo } : {}) };
  } else if (params.summaryType === "community") {
    payload = { ladder_id: ladderId, summary_type: "community", ...(params.weekNo !== undefined ? { week_no: params.weekNo } : {}) };
  } else {
    payload = { ladder_id: ladderId, summary_type: "season" };
  }
  return invokeEdgeFunction<{ message: string }>("generate-ladder-message", payload, true);
};

